
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Alert,
  AlertIcon,
  Badge,
  Text,
} from '@chakra-ui/react'
import { useState, useEffect } from 'react'
import { userActivityService, type LoginHistory as LoginHistoryEntry } from '@/lib/services/userActivityService'
import { useThemeColors } from '@/hooks/useThemeColors'

interface LoginHistoryProps {
  userId: number
}

export function LoginHistory({ userId }: LoginHistoryProps) {
  const colors = useThemeColors()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [loginHistory, setLoginHistory] = useState<LoginHistoryEntry[]>([])

  useEffect(() => {
    loadLoginHistory()
  }, [userId])

  const loadLoginHistory = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await userActivityService.getUserLoginHistory(userId, {
        limit: 50,
      })
      setLoginHistory(response.data)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load login history')
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      success: 'green',
      failed: 'red',
      blocked: 'orange',
      logout: 'gray',
      active: 'blue',
      expired: 'gray',
    }
    return colors[status] || 'gray'
  }

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (loading) {
    return (
      <Box textAlign="center" py={4}>
        <Spinner size="lg" color="blue.500" />
        <Text mt={2}>Loading login history...</Text>
      </Box>
    )
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {error}
      </Alert>
    )
  }

  if (loginHistory.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Text color={colors.text}>
          No login history found for this user
        </Text>
      </Box>
    )
  }

  return (
    <Box overflowX="auto">
      <Table variant="simple" size="sm">
        <Thead>
          <Tr>
            <Th>Date & Time</Th>
            <Th>Type</Th>
            <Th>Status</Th>
            <Th>IP Address</Th>
            <Th>Device</Th>
            <Th>Location</Th>
            <Th>Reason</Th>
          </Tr>
        </Thead>
        <Tbody>
          {loginHistory.map((entry) => (
            <Tr key={entry.id}>
              <Td>{formatDateTime(entry.timestamp)}</Td>
              <Td>
                <Badge colorScheme={getStatusColor(entry.type)}>{entry.type}</Badge>
              </Td>
              <Td>
                <Badge colorScheme={getStatusColor(entry.status)} variant="outline">
                  {entry.status}
                </Badge>
              </Td>
              <Td>{entry.ip_address || 'N/A'}</Td>
              <Td>
                {entry.device_info ? (
                  <Text fontSize="xs">
                    {entry.device_info.device} - {entry.device_info.browser} ({entry.device_info.os})
                  </Text>
                ) : (
                  'N/A'
                )}
              </Td>
              <Td>{entry.location || 'N/A'}</Td>
              <Td>{entry.reason || '-'}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  )
}

